/**
 * An extension of the base Worker class, we need import.meta.url everytime as a base, and Worker needs a URL object so we
 * abstract this away. It also opens up the possibility to extend worker functionality if needed in the future.
 */

import axios, { HttpStatusCode } from "axios";
import { getToken, logout } from "helpers/api/authService";

export type WorkerResponse<DataType, ResponseCallbackDataType = undefined> = {
    data?: DataType;
    httpErrors?: WorkerHttpError[];
    onWorkerResponseCallbackData?: ResponseCallbackDataType;
}

/**
 * Base error type for errors thrown by workers
 */
type WorkerBaseError = {
    message?: string;
}

/**
 * Error that occurred inside the worker is due to a non-2XX HTTP code being received from an API call being performed in the worker.
 */
export type WorkerHttpError = WorkerBaseError & {
    status?: HttpStatusCode;
}

/**
 * Error that occurred inside the worker is due to general unhandled circumstances such as logic errors or array index exceptions etc.
 */
export type WorkerRuntimeError = WorkerBaseError & {
    error?: any;
}

/**
 * General error type that encompasses all error types within a worker.
 * Extend this if more error types are added.
 */
export type WorkerError = WorkerRuntimeError | WorkerHttpError;

/**
 * Base type to extend from when defining types that will be used to pass data TO workers
 */
export type WorkerRequestData<T> = { 
    data: T[];
    error?: string;
    axiosBaseUrl?: string;
    axiosBearerToken?: string | null;
}

/**
 * This model represents a particular worker in a batch of workers (where we have multiple at a time and need a way to differentiate them)
 */
export type WorkerBatchModel<DataType> = {
    batchData: DataType[];
    onmessage: (event: MessageEvent<any>) => void;
    onerror: (event: ErrorEvent) => void;
}

/**
 * Context wrapping type for compiling both worker response as well as any other required data needing to be passed back to the caller of ```runBatchWorkers```.
 */
type WorkerResponseContext<BatchDataType, ResponseType, ResponseCallbackDataType = undefined> = {
    numBatches: number,
    batchIndex: number,
    originalBatchData: BatchDataType[];
    response: WorkerResponse<ResponseType, ResponseCallbackDataType>;
}

/**
 * Handle the errors returned by the Worker Response. Will return a list of errors as strings. If we see a 401 (Unauthorized) we want to initiate the logout process and return undefined.
 */
export function handleWorkerErrors<DataType>(response: WorkerResponse<unknown, unknown>, workers?: WorkerPoolData<DataType>[]): string[] | undefined {
    if (response.httpErrors) {
        let returnedErrors: string[] = [];
        response.httpErrors.forEach((err: WorkerHttpError) => {
            if (err.status === 401) {
                cleanUpWorkers(workers ?? []);
                logout(true, true);
                return undefined;
            } else {
                // Combine all 4XX errors (that aren't 401) into a single error message with comma deliniation. And create a { stockName: X, errorMsg: <The combined error> }, for every stock the batch was given
                (err.status || err.message) && returnedErrors.push(`${err.status && err.status + ": "}${err.message && err.message}`);
            }
        });

        return returnedErrors;
    }
}

function cleanUpWorkers<DataType>(workers: WorkerPoolData<DataType>[]) {
    workers.forEach(w => {
        w.worker.terminate();
        w.worker.onmessage = null;
        w.worker.onerror = null;
        w.data = undefined;
    })
}

type RunWorkersParams<WorkerRequestDataType extends WorkerRequestData<DataType>, DataType, ResponseType, ResponseCallbackDataType = undefined> = {
    workerData: WorkerRequestDataType,
    createWorker: () => Worker,
    batchSize?: number,
    maxActiveWorkers?: number,
    onWorkerResponse?: (
        event: MessageEvent<any>, 
        responseContext: WorkerResponseContext<DataType, ResponseType, ResponseCallbackDataType>, 
        httpErrors: string[] | undefined, 
        promiseReject: (reason?: any) => void
    ) => Promise<ResponseCallbackDataType>, 
    onWorkersSuccess?: (workerResponses: WorkerResponse<ResponseType, ResponseCallbackDataType>[]) => void | Promise<void>,
    onWorkerError?: (error: WorkerError, unprocessedData: DataType[]) => void,
    onWorkersFinished?: (workerResponses: WorkerResponse<ResponseType, ResponseCallbackDataType>[], errorData: { error?: WorkerError, unprocessedData: DataType[]}) => void | Promise<void>, 
}

type InvokeTaskParams = {
    workerPoolIndex?: number;
    terminate?: { action: "resolve" | "reject", reason?: string };
}

type WorkerTask<DataType> = {
    batchData: DataType[];
    invoke: (invokeParams: InvokeTaskParams) => void;
}

type WorkerPoolData<DataType> = {
    worker: Worker;
    /**
     * The data that this worker is currently processing. This is used in the event that we need to terminate the worker and return the data that it was processing.
     */
    data?: DataType[];
}

/**
 * Run batches of workers for specific scripts, each worker is passed ```batchSize``` size of data from the ```WorkerData``` that's passed in
 * @param batchSize The amount of data to pass to a given worker. ```0``` means to effectively use a single worker and give it all the data. (Must be >= 0). E.g. a ```batchSize``` of 100 and passing in a data array of size 450 will result in 5 workers, the first 4 will operate on arrays of size 100 with the last being passed the remaining 50.
 * @param workerData The data that needs to be passed to the Workers. The type of this data must ```extend WorkerData```, the ```data``` field passed in here is what determines the batching of workers. You can pass any other data the worker script needs here, too.
 * @param maxActiveWorkers The maximum number of workers that can be active at a time, if we require more than this number of workers, once a previous worker completes, the next worker will start. ```0``` means no limit. (Must be >= 0).
 * @param createWorker A callback that simply returns a new Worker. E.g. () => new Worker(new URL("./```<SCRIPT_NAME>```", import.meta.url), { type: "module" }), this assumes the script is placed in the same directory as the file that calls ```runBatchWorkers```.
 * @param onWorkerResponse A callback that can be defined by the caller of ```runBatchWorkers``` that gets executed when a worker finishes. The callback will be passed ```responseContext``` which contains response data from the worker as well as any other useful information such as:
 * - ```numBatches```: The number of workers that was created (useful for things like calculating % completion across all workers)
 * - ```originalBatchData```: The batch of data that was sent to this particular worker
 * - ```response```: The response type specified by type definition of ```runBatchWorkers```, must be the same type as the WorkerResponse<```TYPE```> that is returned from the worker
 * The callback will also be passed an array of ```httpErrors``` that were returned from the workers (to perform any custom error handling), as well as ```promiseReject``` callbacks for rejecting the entire callback of the whole worker flow (see ```runBatchWorker``` code where Promise is created)
 * @param onWorkersSuccess A callback that is run when all workers have resolved their promises, it is passed an array of responses from the workers to perform any custom logic.
 * @param onWorkerError A callback that is run when one of the worker promises gets rejected, it is passed a reason.
 * @param onWorkersFinished A callback that is run when either all workers have resolved their promises or a worker promise gets rejected, it is passed all the successful worker responses as well as all unprocessed data.
 */
export function runBatchWorkers<WorkerRequestDataType extends WorkerRequestData<DataType>, DataType, ResponseType, ResponseCallbackDataType = undefined>
(
    {
        workerData, 
        createWorker, 
        batchSize = 100,
        maxActiveWorkers = 20,
        onWorkerResponse, 
        onWorkersSuccess, 
        onWorkerError, 
        onWorkersFinished 
    }: RunWorkersParams<WorkerRequestDataType, DataType, ResponseType, ResponseCallbackDataType>
): { runWorkers: Promise<void>, terminateWorkers: () => void } {

    if (maxActiveWorkers < 0) throw new Error("maxActiveWorkers must be >= 0!");
    if (batchSize < 0) throw new Error("batchSize must be >= 0!");
    
    const data = workerData.data;
    // Split data into batches to pass to workers for processing
    let numBatches = batchSize != 0 ? Math.ceil(data.length / batchSize) : 1;
    let workerPromises: Promise<void>[] = [];

    let workerResponses: WorkerResponse<ResponseType, ResponseCallbackDataType>[] = [];

    let workerQueue: WorkerTask<DataType>[] = [];

    let terminatingWorkers: boolean = false;

    let workerPool: WorkerPoolData<DataType>[] = [];

    const runWorkersFunc: () => Promise<void> = async () => {
        // Create min(numBatches, maxActiveWorkers) workers
        const numWorkers = maxActiveWorkers === 0 ? numBatches : Math.min(numBatches, maxActiveWorkers);
        for (let i = 0; i < numWorkers; i++) {
            // We need to construct the workers on each import wizards page. See here for reasoning: https://webpack.js.org/guides/web-workers/
            // Apparently we can not pass variables into the constructor (even the URL constructor) due to not being able to statically analyze it. Since only the page knows which script to use, we have to create it there.
            workerPool.push({ worker: createWorker()});
        }

        for (let index = 0; index < numBatches; index++) {
            let start = index * batchSize;
            let end = start + batchSize;

            // Get chunk to deploy worker for
            let batch = numBatches == 1 ? data : data.slice(start, end);
    
            const batchWorkerData: WorkerRequestDataType = {
                ...workerData,
                data: batch,
                axiosBearerToken: getToken(),
                axiosBaseUrl: axios.defaults.baseURL
            }
    
            // Set up all workers, we create promises and add them to a list so we can wait for all of them to finish
            workerPromises.push(new Promise<void>((resolve, reject) => { // We create worker promises so we can do something after ALL workers have resolved
                const invoke = ({ workerPoolIndex, terminate }: InvokeTaskParams) => {
                    if (terminate) {
                        switch(terminate.action) {
                            case "resolve":
                                resolve();
                                break;
                            case "reject":
                                reject(terminate.reason);
                                break;
                            default:
                                throw new Error("how dare you, how did you even do this");
                        }
                    }
    
                    if (workerPoolIndex != null && workerPoolIndex < workerPool.length) {
                        workerPool[workerPoolIndex].worker.onmessage = (async event => {
                            const response: WorkerResponse<ResponseType, ResponseCallbackDataType> = event.data;
                            let httpErrors: string[] | undefined = handleWorkerErrors(response, workerPool);
                            // Pages need to do some special logic after a single worker replies, so we await a callback here and pass necessary information in
                            if (onWorkerResponse) response.onWorkerResponseCallbackData = await onWorkerResponse(event, { numBatches: numBatches, originalBatchData: batch, batchIndex: start, response: {...response} }, httpErrors, reject);
        
                            workerResponses.push(response);
        
                            if (!terminatingWorkers) {
                                invokeNextTask(workerPoolIndex);
                            }
                            
                            resolve();
                        });
        
                        // An unhandled (or intentionally thrown) error occurred within the worker. Terminate it and reject the Promise with reason why.
                        workerPool[workerPoolIndex].worker.onerror = (event: ErrorEvent) => {
                            const error: WorkerRuntimeError = { error: event.error, message: event.message };
                            
                            if (!terminatingWorkers) {
                                invokeNextTask(workerPoolIndex);
                            }

                            console.error("An error has occurred within a worker: ", error);
                            reject(error);
                        }

                        workerPool[workerPoolIndex].data = batch;
                        workerPool[workerPoolIndex].worker.postMessage(batchWorkerData);
                    }
                }

                workerQueue.push({ batchData: batch, invoke: invoke });
            }));
        }
    
        // Start the initial tasks
        for (let i = 0; i < numWorkers; i++) {
            if (workerQueue.length <= 0) break;
            invokeNextTask(i);
        }

        function invokeNextTask(workerPoolIndex: number) {
            const workerTask = workerQueue.shift();
            if (workerTask && workerTask.invoke) {
                workerTask.invoke({workerPoolIndex: workerPoolIndex});
            }
        }
        
        /**
         * Terminate all incomplete batch workers.
         * @returns Any data that hasn't been processed (AKA the data that the incomplete workers were responsible for).
         */
        function terminateIncompleteWorkers(): DataType[] {
            const inProgressTaskData = workerPool.flatMap(w => w.data ?? []);
            const queuedTaskData = workerQueue.flatMap(q => q.batchData ?? []);
            
            // Grab the workers that haven't completed yet
            cleanUpWorkers(workerPool);
            // Grab the batch data from these incomplete workers
            return inProgressTaskData.concat(queuedTaskData);
        }
    
        let workerError: WorkerError | undefined;
        // Wait for all workers to finish their scripts
        await Promise.all(workerPromises)
            .then(async () => {
                // This only runs if ALL promises resolved successfully.
                onWorkersSuccess && await onWorkersSuccess(workerResponses);
            }).catch((error: WorkerError) => {
                workerError = error;
                // This runs if ANY promises rejected.
                const unprocessedData = terminateIncompleteWorkers();
                onWorkerError && onWorkerError(error, unprocessedData);
            }).finally(async () => {
                // This runs after the Promise.all is rejected or resolved (ie runs when all are successful or when )
                const unprocessedData = terminateIncompleteWorkers();
                // Once workers are done, run callback. We pass in workerResponses for some pages that don't care about the order that the workers complete but still need
                // a combination of all data that was returned
                onWorkersFinished && await onWorkersFinished(workerResponses, { error: workerError, unprocessedData: unprocessedData });
            });
        
        // Ensure workers are cleaned up just in case
        cleanUpWorkers(workerPool);
    }

    let terminateWorkers!: () => void;

    const runWorkersPromise = new Promise<void>((resolve, reject) => {
        terminateWorkers = () => {
            terminatingWorkers = true;

            for (let index = 0; index < workerQueue.length; index++) {
                const task = workerQueue[index];
                task.invoke(
                    {
                        terminate: {
                            action: "reject",
                            reason: "Process was terminated"
                        }
                    }
                );

                workerQueue.shift();
                index--;
            }

            cleanUpWorkers(workerPool);
            terminatingWorkers = false;
            resolve();
        };
    
        runWorkersFunc()
          .then((result) => {
              resolve(result);
          })
          .catch((error) => {
            reject(error);
          });
      });
    
      return { runWorkers: runWorkersPromise, terminateWorkers: terminateWorkers };
    
}